import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { IProvinceListRequest, IWeekListByMonthRequest, IWeekListRequest } from '@/api/types';
import type {
  DeviceType,
  IApplicationClasses,
  ICalendarDayWidth,
  ICountryForSelects,
  ICurrentWeek,
  IMASUsers,
  IProvinceForSelects,
  IThunkAPI,
  IUtilsState,
  IWeekListByMonth,
  ViewMode,
} from '@/store/types';

import { utils as api } from '@/api';
import {
  getDeviceType,
  getViewMode,
  isActionFromSlice,
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
  logError,
  onFulfilledAction,
  onPendingAction,
  onRejectedAction,
} from '@/helpers';
import { transformWeeksListByMonth } from '@/api/transforms';
import { RootState } from '..';
import { SLICES } from '@/constants/actions';
import { IClassRequest } from '@/api/types';

const initialState: IUtilsState = {
  countriesList: [],
  provincesList: [],
  currentWeek: 0,
  weeks: {},
  weeksByMonth: {},
  calendarDayWidth: [],
  activeModalShiftId: null,
  isMobileScreenSize: false,
  deviceType: getDeviceType(),
  viewMode: getViewMode(),
  showFullscreenLoader: false,
  meta: {
    pending: [],
  },
  masUsersList: [],
  classesList: [],
};

export const getCountriesList = createAsyncThunk<ICountryForSelects[], undefined, IThunkAPI>(
  'vocabcountry/g/list',
  async (_, thunkAPI) => {
    try {
      const response = await api.getCountriesList();
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getMasUsers = createAsyncThunk<IMASUsers[], number, IThunkAPI>(
  'masUsers/list',
  async (campaignId, thunkAPI) => {
    try {
      const response = await api.getMasUsers(campaignId);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getClasses = createAsyncThunk<IApplicationClasses[], IClassRequest, IThunkAPI>(
  'classes/list',
  async (payload, thunkAPI) => {
    try {
      const response = await api.getClasses(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getProvincesList = createAsyncThunk<IProvinceForSelects[], IProvinceListRequest, IThunkAPI>(
  'pub/vocabprovince/g/list',
  async (payload, thunkAPI) => {
    try {
      const response = await api.getProvincesList(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getCurrentWeek = createAsyncThunk<ICurrentWeek, undefined, IThunkAPI>(
  `${SLICES.UTILS}/get/currentWeek`,
  async (_, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getCurrentWeek cancelled');
      });
      const response = await api.getCurrentWeek({ cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      if (state.utils.meta.pending.includes(`${SLICES.UTILS}/get/currentWeek`)) return false;

      return true;
    },
  },
);

export const getWeeksArray = createAsyncThunk<IWeekListByMonth[], IWeekListRequest, IThunkAPI>(
  'weeklabel/time/week/list',
  async (payload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const response = await api.getWeeksArray(payload);
      return transformWeeksListByMonth(response.data, state.auth.user?.timeZoneName);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getWeeksArrayByMonth = createAsyncThunk<IWeekListByMonth[], IWeekListByMonthRequest, IThunkAPI>(
  'weeklabel/time/month',
  async (payload, thunkAPI) => {
    try {
      const response = await api.getWeeksArrayByMonth(payload);
      return transformWeeksListByMonth(response.data);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const setIsMobileScreenSize = createAsyncThunk<
  { deviceType: DeviceType; isMobileScreenSize: boolean; viewMode: ViewMode },
  boolean,
  IThunkAPI
>('weeklabel/testMobile', payload => {
  return { isMobileScreenSize: payload, deviceType: getDeviceType(), viewMode: getViewMode() };
});

export const slice = createSlice({
  name: 'utils',
  initialState,
  reducers: {
    clearActiveModalShiftId(state) {
      state.activeModalShiftId = null;
    },
    clearCalendarDayWidth(state) {
      state.calendarDayWidth = [];
    },
    clearCountriesProvinces(state) {
      state.countriesList = [];
      state.provincesList = [];
    },
    clearCurrentWeek(state) {
      state.currentWeek = 0;
    },
    clearWeekArray(state) {
      state.weeks = {};
    },
    clearWeekArrayByMonth(state) {
      state.weeksByMonth = {};
    },
    setCalendarDayWidth(state, action: PayloadAction<ICalendarDayWidth>) {
      state.calendarDayWidth = [
        ...state.calendarDayWidth.filter(item => item.dayNumber !== action.payload.dayNumber),
        action.payload,
      ];
    },
    setActiveModalShiftId(state, action: PayloadAction<number>) {
      state.activeModalShiftId = action.payload;
    },
    showFullscreenLoader(state) {
      state.showFullscreenLoader = true;
    },
    hideFullscreenLoader(state) {
      state.showFullscreenLoader = false;
    },
    updateViewMode(state) {
      state.viewMode = getViewMode();
    },
    setMASUserList(state, { payload }) {
      state.masUsersList = [...payload];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(setIsMobileScreenSize.fulfilled, (state, action) => {
        state.isMobileScreenSize = action.payload.isMobileScreenSize;
        state.deviceType = action.payload.deviceType;
        state.viewMode = action.payload.viewMode;
      })
      .addCase(getCountriesList.fulfilled, (state, action) => {
        state.countriesList = action.payload;
      })
      .addCase(getProvincesList.fulfilled, (state, action) => {
        state.provincesList = action.payload;
      })
      .addCase(getCurrentWeek.fulfilled, (state, action) => {
        state.currentWeek = action.payload.weekId;
      })
      .addCase(getWeeksArray.fulfilled, (state, action) => {
        state.weeks = action.payload.reduce<IUtilsState['weeks']>((acc, item) => {
          acc[item.weekId] = item;
          return acc;
        }, {});
      })
      .addCase(getWeeksArrayByMonth.fulfilled, (state, action) => {
        state.weeksByMonth = action.payload.reduce<IUtilsState['weeksByMonth']>((acc, item) => {
          acc[item.weekId] = item;
          return acc;
        }, {});
      })
      .addCase(getMasUsers.fulfilled, (state, action) => {
        state.masUsersList = action.payload;
      })
      .addCase(getClasses.fulfilled, (state, action) => {
        state.classesList = action.payload.reduce(
          (accumulator: IApplicationClasses[], currentValue: IApplicationClasses) => {
            if (!accumulator.find(f => f.classId === currentValue.classId)) {
              return [...accumulator, currentValue];
            }
            return accumulator;
          },
          [],
        );
      })
      .addMatcher(action => isFulfilledAction(action) && isActionFromSlice(action, SLICES.UTILS), onFulfilledAction)
      .addMatcher(action => isRejectedAction(action) && isActionFromSlice(action, SLICES.UTILS), onRejectedAction)
      .addMatcher(action => isPendingAction(action) && isActionFromSlice(action, SLICES.UTILS), onPendingAction);
  },
});

// actions
export const {
  clearActiveModalShiftId,
  clearCalendarDayWidth,
  clearCountriesProvinces,
  clearWeekArray,
  clearWeekArrayByMonth,
  hideFullscreenLoader,
  setActiveModalShiftId,
  setCalendarDayWidth,
  showFullscreenLoader,
  updateViewMode,
  setMASUserList,
} = slice.actions;

export const masUsersList = (state: RootState): IUtilsState['masUsersList'] => state.utils.masUsersList;
export const classesList = (state: RootState): IUtilsState['classesList'] => state.utils.classesList;

export default slice.reducer;
